import { cn } from "@/libs/cn";
import React from "react";
import { InputMask } from "@react-input/mask";

const DIGIT_REPLACEMENT = { _: /\d/ };

export type InputType = "text" | "tel" | "cnpj" | "cpf" | "postalCode" | "email" | "percentage" | "fare" | "radio";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  disabled?: boolean;
  label?: string;
  iconPosition?: "left" | "right";
  icon?: React.ReactNode;
  maskType?: InputType;
  markAsRequired?: boolean;
}

const getMask = (type?: InputType): string => {
  switch (type) {
    case "tel":
      return "(__) _____-____";
    case "cnpj":
      return "__.___.___/____-__";
    case "cpf":
      return "___.___.___-__";
    case "postalCode":
      return "_____-___";
    case "percentage":
      return "__%";
    case "fare":
      return "R$/kWh _._______";
    default:
      return "";
  }
};

export const inputClassNameValues =
  "w-full p-3 h-11 border border-ui-border-muted bg-white rounded-lg text-sm outline-none placeholder:text-sm placeholder:font-normal placeholder:text-[#ADB7D3] focus:border-ui-primary/30 disabled:bg-gray-100 disabled:cursor-not-allowed";

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, icon, iconPosition = "left", maskType = "text", markAsRequired, ...props }, ref) => {
    const mask = getMask(maskType);

    const inputClassName = cn(
      inputClassNameValues,
      icon && iconPosition === "left" && "pl-12",
      icon && iconPosition === "right" && "pr-12",
      className,
    );

    return (
      <div className="min-h-10 items-center">
        {label && maskType !== "radio" && (
          <label className="text-sm">
            {label}
            {markAsRequired && <span className="text-red-500"> *</span>}
          </label>
        )}

        <div className="relative">
          {icon && (
            <div className={cn("absolute top-1/2 -translate-y-1/2 text-[#0B4A95]", iconPosition === "right" ? "right-4" : "left-4")}>
              {icon}
            </div>
          )}

          {mask ? (
            <InputMask
              mask={mask}
              replacement={DIGIT_REPLACEMENT}
              type={maskType === "email" ? "email" : "text"}
              className={inputClassName}
              ref={ref}
              {...props}
            />
          ) : maskType === "radio" ? (
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="radio" className={inputClassName} {...props} ref={ref} />
              <span>{label}</span>
            </label>
          ) : (
            <input type={maskType} className={inputClassName} {...props} ref={ref} />
          )}
        </div>
      </div>
    );
  },
);

Input.displayName = "Input";

export default Input;
